/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SWRConfig } from "swr";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, Security, useOktaAuth } from "@okta/okta-react";

import { UserService } from "./services/UserService";
import { config } from "./config";
import { AuthTokenError, IdentityRepository } from "./repositories/IdentityRepository";
import { UserProvider } from "./providers/UserProvider";
import { isEuEnabledCached } from "./feature-flags/euFeatureFlag";
import {
    clearLoginFlowMarkers,
    clientIdForIssuer,
    getIssuerFromStoredToken,
    getReturnPath,
    handleCallbackError,
    LOGIN_HINT_KEY,
    resolveOktaConfig,
    resolveRegionFromLoginHint,
    setPendingRegion,
    SILENT_PROBE_DONE_KEY
} from "./lib/region-auth";
import { ServicesProvider } from "./providers/ServicesProvider";
import UserGroups from "./ui/pages/UserGroups/UserGroups";
import { Invitations } from "./ui/pages/Invitations/Invitations";
import { BulkUploads } from "./ui/pages/BulkUploads/BulkUploads";
import { Roles } from "./ui/pages/Roles/Roles";
import { AccessManagementProducts } from "./ui/pages/Access/AccessManagementProducts";
import { NotFound } from "./ui/pages/NotFound/NotFound";
import { Error404 } from "./ui/pages/NotFound/Error404";
import { Unauthorized } from "./ui/pages/Unauthorized/Unauthorized";
import { RequireAuth } from "./ui/components/Auth/RequireAuth";
import { Logout } from "./ui/components/Auth/Logout";
import { LoadingIndicator } from "./ui/components/LoadingIndicator/LoadingIndicator";
import { SSOSetup } from "./ui/pages/SSOSetup/SSOSetup";
import { Feedback, FeedbackTypes } from "@optimizely/axiom";
import { ACCESS_TYPES, ATTRIBUTE_ROLES, TOASTER_EVENT_NAME } from "./constants/constants";
import { ROUTE_VIEW_MAPPINGS } from "./constants/route-constants";
import { Usage } from "./ui/pages/Usage/Usage";
import { datadogRum } from "@datadog/browser-rum";
import { AttributeService } from "./services/AttributeService";
import "./globals.scss";
import { AccessManagementProductInstances } from "./ui/pages/Access/AccessManagementProductInstances";
import { AccessManagementProductInstanceProjects } from "./ui/pages/Access/AccessManagementProductInstanceProjects";
import { AccessManagementProjectUsers } from "./ui/pages/Access/AccessManagementProjectUsers";
import { AccessManagementUsers } from "./ui/components/Access/AccessManagementUsers";
import { InstanceService } from "./services/InstanceService";
import { Access } from "./ui/pages/Support/Access/Access";
import { SupportAccess } from "./ui/pages/Support/Access/SupportAccess";
import { AccountActivation } from "./ui/pages/Support/AccountActivation/AccountActivation";
import { DeveloperSettings } from "./ui/pages/Support/DeveloperSettings/DeveloperSettings";
import { InvitationService } from "./services/InvitationService";
import { CustomerService } from "./services/CustomerService";
import { MFA } from "./ui/pages/MFA/MFA";
import { SCIM } from "./ui/pages/Settings/SCIM/SCIM";
import { NavigateToAuthorized } from "./ui/components/Auth/NavigateToAuthorized";
import { IdpService } from "./services/Idp/IdpService";
import { DomainsPage } from "./ui/pages/Domains/Domains";
import { AccessManagementInstanceDetails } from "./ui/components/Access/AccessManagementInstanceDetails";
import { AccessManagementUserFormProvider } from "./ui/components/Access/AccessManagementUserForm/AccessManagementUserFormProvider";
import { MasterDashboard } from "./ui/pages/Support/MasterDashboard/MasterDashboard";
import { UserMigrationPage } from "./ui/pages/UserMigration/UserMigration";
import { UserManagementRouting } from "./ui/components/RoutingRules/UserManagementRouting/UserManagementRouting";
import { Flags } from "./feature-flags/flags";
import { OrganizationService } from "./services/Organization/OrganizationService";
import { UpdateSynchronizer } from "./ui/components/UpdateSynchronizer/UpdateSynchronizer";
import { AnalyticsTracking } from "./ui/components/AnalyticsTracking/AnalyticsTracking";
import { SettingsRouting } from "./ui/components/RoutingRules/SettingsRouting/SettingsRouting";
import { NotificationService } from "./services/Notifications/NotificationsService";
import { UserFlow } from "./ui/components/UserFlow/UserFlow";
import { OrganizationSettingsService } from "./services/OrganizationSettings/OrganizationSettingsService";
import { AuthorizedPageAccess } from "./ui/components/AuthorizedPageAccess/AuthorizedPageAccess";
import { FeatureFlagSwitch } from "./ui/components/FeatureFlagSwitch/FeatureFlagSwitch";
import { GenAIPage } from "./ui/pages/Settings/GenAI/GenAIPage";
import { BillingNotificationsPage } from "./ui/pages/Settings/BillingNotifications/BillingNotificationsPage";
import { OrganizationPage } from "./ui/pages/Settings/Organization/OrganizationPage";
import { OpalRoleSuggestionEnablementPage } from "./ui/pages/Settings/OpalAutoProvisioning/OpalRoleSuggestionEnablementPage";
import { UserPermissions } from "./ui/pages/UserPermissions/UserPermissions";
import { TeamsPage } from "./ui/pages/Teams/TeamsPage";
import { TeamsService } from "./services/Teams/TeamsService";
import { AccessManagementTeams } from "./ui/pages/Access/AccessManagementTeams";
import { RebrandFavicon } from "./ui/components/RebrandFavicon/RebrandFavicon";
import { RebrandTheme } from "./ui/components/RebrandTheme/RebrandTheme";
import { ForethoughtWidget } from "./ui/components/ForethoughtWidget/ForethoughtWidget";
import { AgentsPage } from "./ui/pages/Agents/AgentsPage";
import { AgentsRouteGuard } from "./ui/components/AgentsRouteGuard/AgentsRouteGuard";

datadogRum.init({
    applicationId: config.DATADOG_APP_ID!,
    clientToken: config.DATADOG_CLIENT_TOKEN!,
    site: "us3.datadoghq.com",
    service: "admin-center",
    env: config.APP_ENVIRONMENT,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    trackFrustrations: true,
    defaultPrivacyLevel: "mask-user-input"
});

datadogRum.startSessionReplayRecording();

const BASE_URL = config.SERVICE_URL;

/**
 * Direct-region auth (EU data residency) is gated by the enable_eu flag. The flag is
 * resolved pre-auth and anonymously (see feature-flags/euFeatureFlag) because every
 * path below runs before <Security> has a token, which is where useFeatureFlag would
 * need one.
 *
 * Flag OFF => the default issuer + client, exactly as before direct-region auth existed.
 * Flag ON  => the region that minted the current token, else the pending target the
 *             broker resolved for this login.
 */
const euEnabled = isEuEnabledCached();

/**
 * Silent pre-auth resolution. If not authenticated and arrived with a login_hint (from
 * the Home product tile), ask the broker which region the user belongs to, set the
 * transient pending target, and reload so OktaAuth rebuilds against that region.
 * signInWithRedirect then goes to the region Okta — which already holds the user's
 * session from the Home login — so it returns a code SILENTLY. That is the cross-app SSO.
 *
 * Returns true if a reload is in flight (caller should stop rendering). Guarded by a
 * module-scope latch so the fetch can never fire twice for one page load.
 */
let regionBootstrapStarted = false;
function bootstrapRegionFromLoginHint(): boolean {
    // Only a real token blocks the bootstrap, not hasActiveRegion(): a pending target
    // left by an abandoned attempt in this tab (user backed out of the region login and
    // clicked a Home tile again) would otherwise override the fresh hint and send the
    // user to the previous attempt's region.
    if (!euEnabled || regionBootstrapStarted || getIssuerFromStoredToken()) return false;
    const loginHint = new URLSearchParams(window.location.search).get("login_hint");
    if (!loginHint || !config.BROKER_URL) return false;
    regionBootstrapStarted = true;
    // An explicit hint starts a NEW login: drop the stale pending target and loop guards
    // so the probe runs fresh against the region resolved below.
    clearLoginFlowMarkers();
    // Stash the hint NOW — we strip it from the URL below, so signIn must recover it
    // from sessionStorage after the rebuild reload.
    window.sessionStorage.setItem(LOGIN_HINT_KEY, loginHint);
    void resolveRegionFromLoginHint(loginHint).then((issuer) => {
        const clientId = issuer ? clientIdForIssuer(issuer) : undefined;
        if (issuer && clientId) {
            setPendingRegion(issuer, clientId);
        } else if (!issuer) {
            // The broker could not name a region: unreachable, non-ok, timed out, or it
            // answered without an issuer. Silent SSO is off the table; the reload below
            // drops us on the broker's email page instead.
            //
            // Never attach the login hint — it is the user's email.
            datadogRum.addError(new Error("Broker could not resolve a region from login_hint"), {
                component: "bootstrapRegionFromLoginHint"
            });
        } else {
            // Resolved fine, but Admin Center has no client id for that region — e.g.
            // the per-region SPA app is not provisioned in this environment yet. The
            // user still ends up on the broker email page, so WITHOUT this the symptom
            // is indistinguishable from a broker failure, even though the cause is our
            // own configuration. (LoginRegion reports the same gap on its own path.)
            datadogRum.addError(new Error("No Admin Center client id configured for the resolved region"), {
                component: "bootstrapRegionFromLoginHint",
                issuer
            });
        }
        const url = new URL(window.location.href);
        url.searchParams.delete("login_hint");
        window.location.replace(url.toString());
    });
    return true;
}

const { issuer: activeIssuer, clientId: activeClientId } = resolveOktaConfig(euEnabled);
const oktaAuth = new OktaAuth({
    issuer: activeIssuer,
    clientId: activeClientId,
    redirectUri: config.REDIRECT_URI,
    scopes: config.SCOPE,
    pkce: config.PKCE,
    responseType: config.RESPONSE_TYPE,
    tokenManager: {
        autoRenew: true
    },
    services: {
        autoRenew: false,
        autoRemove: false
    },
    storageManager: {
        token: {
            storageTypes: ["sessionStorage", "memory"]
        },
        cache: {
            storageTypes: ["sessionStorage", "memory"]
        },
        transaction: {
            storageTypes: ["sessionStorage", "memory"]
        }
    }
});

// Create a route and context wrapper here so that services are only instantiated
// inside the <Security /> context provided in the exported component.
/**
 * Landing point for the broker email-page fallback (direct-nav case). The broker 302s
 * here with ?issuer=&region=&login_hint=. We map the region issuer to AC's OWN
 * per-region client_id (the broker deliberately sends no client id), store both as a
 * transient target, then hard-navigate to "/" so the module-scope OktaAuth rebuilds.
 */
const LoginRegion = () => {
    const query = new URLSearchParams(window.location.search);
    const issuer = query.get("issuer");
    const acClientId = issuer ? clientIdForIssuer(issuer) : undefined;
    const resolved = Boolean(issuer && acClientId);

    useEffect(() => {
        if (!resolved) {
            // Do NOT navigate to "/" here. Nothing was stored, so RequireAuth would send
            // the user straight back to the broker, which would return here and fail the
            // same way — an infinite redirect loop. Stop and surface it.
            datadogRum.addError(new Error("Broker returned an unusable region issuer"), {
                component: "LoginRegion",
                issuer: issuer ?? "(missing)"
            });
            return;
        }
        setPendingRegion(issuer as string, acClientId as string);
        // The user came through the broker email page ON PURPOSE to log in, so skip the
        // silent prompt=none probe (which would fail with no session and bounce back to
        // the broker). Mark the probe as done so signIn() does a direct INTERACTIVE
        // login at the region. Stash login_hint so signIn can recover it even if the
        // router drops the query string first.
        window.sessionStorage.setItem(SILENT_PROBE_DONE_KEY, "1");
        const loginHint = query.get("login_hint");
        if (loginHint) {
            window.sessionStorage.setItem(LOGIN_HINT_KEY, loginHint);
        }
        // Return to the route the user originally asked for, not "/" — the broker hop
        // happens before any OIDC redirect, so setOriginalUri cannot preserve it.
        //
        // The hint is deliberately NOT put back on the URL: useSession recovers it from
        // sessionStorage, and carrying it in the query would both expose the email in
        // the address bar and make setOriginalUri persist that PII-bearing URL as the
        // post-login destination.
        window.location.assign(getReturnPath());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved]);

    if (!resolved) {
        return (
            <div data-testid="login-region-error">
                <p>We could not determine which region to sign you in to.</p>
                <p>
                    Please try again, or{" "}
                    <a href="https://support.optimizely.com/hc/en-us/requests/new" rel="noreferrer" target="_blank">
                        submit a support ticket
                    </a>{" "}
                    if the problem continues.
                </p>
            </div>
        );
    }
    return <LoadingIndicator logo />;
};

/**
 * Error UI for the OIDC callback. The silent-SSO probe (signIn with prompt=none)
 * returns ?error=login_required here when the region Okta has NO active session.
 * In that case we do NOT show the region's hosted login — we send the user to the
 * broker email page (single ingress). Any other error rethrows to the default.
 *
 * Exported for unit tests: it is only ever wired in as LoginCallback's errorComponent,
 * and reaching it through <App /> would mean re-importing this module per flag state.
 */
export const CallbackErrorHandler = ({ error }: { error: Error }) => {
    const { oktaAuth: oktaAuthFromContext } = useOktaAuth();
    useEffect(() => {
        // The loop-detection protocol itself lives in region-auth. All this component
        // contributes is the pre-probe URI, which only okta-react's context can supply.
        handleCallbackError(error, oktaAuthFromContext.getOriginalUri());
    }, [error, oktaAuthFromContext]);
    return <LoadingIndicator logo />;
};

const AppRoutesWithContext = () => {
    const { oktaAuth: oktaAuthFromContext } = useOktaAuth();
    const location = useLocation();
    const [toast, setToast] = useState<{ type: FeedbackTypes | undefined; message: string } | null>(null);
    const navigate = useNavigate();

    const getAccessToken = useCallback(() => oktaAuthFromContext.getOrRenewAccessToken(), [oktaAuthFromContext]);
    const identityRepository = useMemo(() => new IdentityRepository(BASE_URL, getAccessToken), [getAccessToken]);

    const serviceContextValue = useMemo(
        () => ({
            identityRepository,
            customerService: new CustomerService(identityRepository),
            userService: new UserService(identityRepository),
            attributeService: new AttributeService(identityRepository),
            idpService: new IdpService(identityRepository),
            instanceService: new InstanceService(identityRepository),
            invitationService: new InvitationService(identityRepository),
            notificationsService: new NotificationService(identityRepository),
            organizationService: new OrganizationService(identityRepository),
            organizationSettingsService: new OrganizationSettingsService(identityRepository),
            teamsService: new TeamsService(identityRepository)
        }),
        [identityRepository]
    );

    const handleToast = useCallback((eventDetails: any) => {
        const { detail } = eventDetails || {};
        setToast(detail);
    }, []);

    const handleLogout = useCallback(
        async (e?: Event) => {
            // eslint-disable-next-line no-console
            console.warn("[eu-debug] /logout via COMMON HEADER event", e?.type, (e as CustomEvent)?.detail);
            navigate("/logout");
        },
        [navigate]
    );

    useEffect(() => {
        document.addEventListener(TOASTER_EVENT_NAME, handleToast as EventListener);
        return () =>
            document.removeEventListener(TOASTER_EVENT_NAME, handleToast as EventListenerOrEventListenerObject);
    }, [handleToast]);

    useEffect(() => {
        window.addEventListener("@opti-common-header:logout", handleLogout as EventListenerOrEventListenerObject);

        return () => {
            window.removeEventListener(
                "@opti-common-header:logout",
                handleLogout as EventListenerOrEventListenerObject
            );
        };
    }, [handleLogout]);

    useEffect(() => {
        let viewName;
        const productBasedFlowRoute = /\/access\/products/;

        if (productBasedFlowRoute.test(location.pathname)) {
            viewName = location.pathname;
        } else {
            viewName = ROUTE_VIEW_MAPPINGS[location.pathname]?.name || location.pathname.split("/").pop();
        }

        datadogRum.startView({
            name: viewName
        });
    }, [location]);

    return (
        <>
            <ServicesProvider services={serviceContextValue}>
                <UserProvider>
                    <UserFlow />
                    <UpdateSynchronizer />
                    <AnalyticsTracking />
                    <RebrandFavicon />
                    <RebrandTheme />
                    <ForethoughtWidget />
                    <Routes>
                        <Route
                            element={
                                <LoginCallback
                                    loadingElement={<LoadingIndicator logo />}
                                    // Flag off: no silent probe runs, so there is no
                                    // login_required to intercept — keep okta-react's
                                    // default error UI exactly as before.
                                    {...(euEnabled && { errorComponent: CallbackErrorHandler })}
                                />
                            }
                            path="/callback"
                        />
                        {euEnabled && <Route element={<LoginRegion />} path="/login-region" />}
                        {euEnabled && <Route element={<Unauthorized />} path="/unauthorized-app" />}
                        <Route element={<Logout />} path="/logout" />
                        <Route element={<RequireAuth />} path="/">
                            <Route element={<NavigateToAuthorized />} path="/" />

                            <Route
                                path="access"
                                element={
                                    <AuthorizedPageAccess
                                        action={ATTRIBUTE_ROLES.PRODUCTS.READ}
                                        featureFlag={Flags.ENABLE_ACCESS_MANAGEMENT}
                                    />
                                }
                            >
                                <Route element={<Navigate to="/access/products" replace />} path="" />
                                <Route element={<AccessManagementProducts />} path="products" />
                                <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.USERS.READ} />}>
                                    <Route
                                        element={
                                            <AccessManagementUserFormProvider>
                                                <AccessManagementUsers />
                                            </AccessManagementUserFormProvider>
                                        }
                                        path="users"
                                    />
                                </Route>
                                <Route path="products/:productId">
                                    <Route element={<AccessManagementProductInstances />} path="instances" />
                                    <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.USERS.READ} />}>
                                        <Route
                                            element={
                                                <AccessManagementUserFormProvider>
                                                    <AccessManagementUsers />
                                                </AccessManagementUserFormProvider>
                                            }
                                            path="users"
                                        />
                                    </Route>
                                    <Route path="instances/:instanceId">
                                        <Route element={<AccessManagementProductInstanceProjects />} path="projects" />
                                        <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.USERS.READ} />}>
                                            <Route
                                                element={
                                                    <AccessManagementUserFormProvider>
                                                        <AccessManagementUsers />
                                                    </AccessManagementUserFormProvider>
                                                }
                                                path="users"
                                            />
                                        </Route>
                                        <Route
                                            path="teams"
                                            element={
                                                <FeatureFlagSwitch
                                                    featureFlag={Flags.ENABLE_TEAM_GROUPS}
                                                    onElement={
                                                        <AuthorizedPageAccess action={ATTRIBUTE_ROLES.GROUPS.READ} />
                                                    }
                                                    offElement={<Unauthorized />}
                                                />
                                            }
                                        >
                                            <Route element={<AccessManagementTeams />} path="" />
                                        </Route>
                                        <Route
                                            element={
                                                <AuthorizedPageAccess
                                                    action={ATTRIBUTE_ROLES.INSTANCE_DETAILS.UPDATE}
                                                />
                                            }
                                        >
                                            <Route element={<AccessManagementInstanceDetails />} path="details" />
                                        </Route>
                                        <Route
                                            path="projects/:projectId"
                                            element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.USERS.READ} />}
                                        >
                                            <Route
                                                element={
                                                    <AccessManagementUserFormProvider>
                                                        <AccessManagementProjectUsers />
                                                    </AccessManagementUserFormProvider>
                                                }
                                                path="users"
                                            />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>

                            <Route element={<UserManagementRouting />} path="user-management">
                                <Route element={<Navigate to="/user-management/users" replace />} path="" />
                                <Route
                                    element={
                                        <AccessManagementUserFormProvider>
                                            <AccessManagementUsers hideRevoke disableDeleteFromOptiId />
                                        </AccessManagementUserFormProvider>
                                    }
                                    path="users"
                                />
                                <Route element={<Roles />} path="roles" />
                                <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.INVITATIONS.READ} />}>
                                    <Route
                                        element={
                                            <AuthorizedPageAccess
                                                action={ATTRIBUTE_ROLES.USERS.READ}
                                                scopedAccess={ACCESS_TYPES.SUPER_ADMIN}
                                            />
                                        }
                                    >
                                        <Route
                                            element={
                                                <AccessManagementUserFormProvider>
                                                    <Invitations />
                                                </AccessManagementUserFormProvider>
                                            }
                                            path="invitations"
                                        />
                                    </Route>
                                </Route>
                                {/* Renders the Unauthorized page when the flag is off OR the user is not a Super Admin. */}
                                <Route
                                    element={
                                        <AuthorizedPageAccess
                                            featureFlag={Flags.ENABLE_BULK_DISPLAY}
                                            action={ATTRIBUTE_ROLES.USERS.READ}
                                            scopedAccess={ACCESS_TYPES.SUPER_ADMIN}
                                        />
                                    }
                                >
                                    <Route element={<BulkUploads />} path="uploads" />
                                </Route>
                            </Route>

                            <Route path="teams" element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.GROUPS.READ} />}>
                                <Route
                                    element={
                                        <FeatureFlagSwitch
                                            featureFlag={Flags.ENABLE_TEAM_GROUPS}
                                            onElement={<TeamsPage />}
                                            offElement={<Error404 />}
                                        />
                                    }
                                    path=""
                                />
                            </Route>

                            <Route
                                path="agents"
                                element={
                                    <FeatureFlagSwitch
                                        featureFlag={Flags.ENABLE_VIRTUAL_AGENT_USERS}
                                        onElement={
                                            <AuthorizedPageAccess
                                                action={ATTRIBUTE_ROLES.USERS.READ}
                                                scopedAccess={ACCESS_TYPES.SUPER_ADMIN}
                                            />
                                        }
                                        offElement={<Error404 />}
                                    />
                                }
                            >
                                <Route element={<AgentsRouteGuard />}>
                                    <Route
                                        element={
                                            <AccessManagementUserFormProvider>
                                                <AgentsPage />
                                            </AccessManagementUserFormProvider>
                                        }
                                        path=""
                                    />
                                </Route>
                            </Route>

                            <Route path="roles">
                                <Route element={<Roles />} path="" />
                            </Route>

                            <Route
                                path="usage"
                                element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.BILLING.READ} />}
                            >
                                <Route element={<Usage />} path="" />
                            </Route>

                            <Route element={<SettingsRouting />} path="account-management">
                                <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.SETTINGS.UPDATE} />}>
                                    <Route element={<Navigate to="/account-management/sso" replace />} path="" />
                                    <Route element={<SSOSetup />} path="sso" />
                                    <Route element={<DomainsPage />} path="domains" />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_SCIM_UI}
                                                onElement={<SCIM />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="scim"
                                    />
                                    <Route element={<MFA />} path="mfa" />
                                </Route>
                            </Route>

                            <Route path="settings" element={<SettingsRouting />}>
                                <Route element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.SETTINGS.UPDATE} />}>
                                    <Route element={<Navigate to="/settings/authentication/sso" replace />} path="" />
                                    <Route element={<SSOSetup />} path="authentication/sso" />
                                    <Route element={<DomainsPage />} path="authentication/domains" />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_SCIM_UI}
                                                onElement={<SCIM />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="authentication/scim"
                                    />
                                    <Route element={<MFA />} path="authentication/mfa" />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_GEN_AI}
                                                onElement={<GenAIPage />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="genai"
                                    />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_OPAL_AUTO_PROVISIONING}
                                                onElement={<OpalRoleSuggestionEnablementPage />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="genai/products-instances"
                                    />
                                    <Route
                                        element={<Navigate to="/settings/notifications/billing" replace />}
                                        path="notifications"
                                    />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_BILLING_NOTIFICATION_SETTINGS}
                                                onElement={<BillingNotificationsPage />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="notifications/billing"
                                    />
                                    <Route
                                        element={
                                            <FeatureFlagSwitch
                                                featureFlag={Flags.ENABLE_DEVELOPER_SETTINGS}
                                                onElement={<OrganizationPage />}
                                                offElement={<Error404 />}
                                            />
                                        }
                                        path="organization"
                                    />
                                </Route>
                            </Route>

                            <Route
                                element={<AuthorizedPageAccess featureFlag={Flags.ENABLE_SUPPORT_FEATURES} />}
                                path="support"
                            >
                                <Route element={<Navigate to="/support/access" replace />} path="" />
                                <Route
                                    element={
                                        <FeatureFlagSwitch
                                            featureFlag={Flags.SUPPORT_PAGE_UPDATES}
                                            onElement={<SupportAccess />}
                                            offElement={<Access />}
                                        />
                                    }
                                    path="access"
                                />
                                <Route element={<Usage enableCustomerSelection />} path="usage" />
                                <Route element={<AccountActivation />} path="account-activation" />
                                <Route element={<MasterDashboard />} path="master-dashboard" />
                                <Route
                                    element={
                                        <FeatureFlagSwitch
                                            featureFlag={Flags.ENABLE_DEVELOPER_SETTINGS}
                                            onElement={<DeveloperSettings />}
                                            offElement={<Error404 />}
                                        />
                                    }
                                    path="developer-settings"
                                />
                                <Route element={<UserMigrationPage />} path="user-migration" />
                                <Route
                                    element={
                                        <FeatureFlagSwitch
                                            featureFlag={Flags.ENABLE_INSTANCE_PERMISSIONS_TAB}
                                            onElement={<UserPermissions />}
                                            offElement={<Error404 />}
                                        />
                                    }
                                    path="user-permissions"
                                />
                            </Route>

                            <Route
                                element={<AuthorizedPageAccess action={ATTRIBUTE_ROLES.GROUPS.READ} />}
                                path="groups"
                            >
                                <Route element={<UserGroups />} path="" />
                            </Route>
                        </Route>
                        <Route path="*" element={<NotFound />}></Route>
                    </Routes>
                </UserProvider>
            </ServicesProvider>
            {toast && (
                <Feedback
                    onClose={() => setToast(null)}
                    open
                    type={toast.type || "info"}
                    // @ts-ignore-next-line
                    position="top-right"
                >
                    {toast.message}
                </Feedback>
            )}
        </>
    );
};

export const App = () => {
    const navigate = useNavigate();
    const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
        navigate(toRelativeUrl(originalUri || "/", window.location.origin), { replace: true });
    };

    // Direct-region: if we arrived with a login_hint but no resolved region yet,
    // resolve it silently and reload before any auth kicks off. When this returns
    // true a reload is in flight — render nothing to avoid a premature signIn
    // against the broker fallback issuer.
    if (bootstrapRegionFromLoginHint()) {
        return <LoadingIndicator logo />;
    }

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <SWRConfig
                value={{
                    revalidateIfStale: false,
                    revalidateOnFocus: false,
                    onError: (error) => {
                        // A missing token before sign-in is not an expired session. On a
                        // tab's first visit RequireAuth awaits the enable_eu decision before
                        // signing in, and anything that fetches meanwhile (e.g. UserProvider
                        // acting on ?orgId=) throws AuthTokenError. Logging out then would
                        // drop the login_hint and send the user to the wrong region.
                        const wasAuthenticated = oktaAuth.authStateManager.getAuthState()?.isAuthenticated;
                        if ((error instanceof AuthTokenError && wasAuthenticated) || error.xhr?.status === 401) {
                            // eslint-disable-next-line no-console
                            console.warn(
                                "[eu-debug] /logout via SWR onError",
                                error?.name,
                                error?.message,
                                error?.xhr?.status,
                                error?.xhr?.url ?? error?.url,
                                {
                                    tokenIss: JSON.parse(window.sessionStorage.getItem("okta-token-storage") || "{}")
                                        ?.accessToken?.claims?.iss
                                }
                            );
                            navigate("/logout");
                        }
                    }
                }}
            >
                <AppRoutesWithContext />
            </SWRConfig>
        </Security>
    );
};

export default App;
